// Sheet (bottom-sheet modal) — Preact-rendered chrome.
//
// 정적 HTML로 박혀 있던 `#sheet` / `#sheet-backdrop`를 Preact 컴포넌트로
// 옮긴다. 외부 API(`openSheet`/`closeSheet`)는 그대로 둔 채 내부 chrome만
// JSX로 갈아끼우는 점진적 전환 — 시트를 호출하는 수십 곳은 변경 없음.
//
// 본문(body)·푸터(footer)는 여전히 imperative DOM(Child) 계약을 유지하므로
// ref 컨테이너에 appendChildren으로 주입한다. 시트 호출자들이 Preact VNode를
// 넘기게 바꾸는 건 별도 단계.
//
// 백드롭 클릭·ESC 닫기는 컴포넌트 안에서 직접 처리 — legacy.ts의 bindGlobals
// 핸들러 두 개가 사라짐.
//
// 마운트는 main.ts에서 `mountSheet(document.getElementById("sheet-root"))`.

import { Fragment, h, render } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { appendChildren, type Child } from "./dom";

interface SheetState {
  open: boolean;
  title: string;
  body: Child;
  footer: Child;
  // onClose가 있으면 X/ESC/백드롭 클릭 시 close 대신 onClose가 실행됨.
  // multi-step 시트(예: 템플릿 리스트 → 폼)에서 폼의 X를 "리스트로 돌아가기"로
  // 만들 때 사용. 안 넘기면 기본 동작(닫기).
  onClose?: () => void;
}

const INITIAL: SheetState = { open: false, title: "", body: null, footer: null };

let currentState: SheetState = INITIAL;
let publish: ((s: SheetState) => void) | null = null;

export function setSheetState(next: Partial<SheetState>): void {
  currentState = { ...currentState, ...next };
  publish?.(currentState);
}

export function getSheetState(): SheetState {
  return currentState;
}

function Sheet() {
  const [state, setState] = useState<SheetState>(currentState);
  publish = setState;

  const bodyRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return;
    node.innerHTML = "";
    if (state.body != null && state.body !== false) appendChildren(node, state.body);
    if (state.open) {
      node.scrollTop = 0;
      requestAnimationFrame(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
      });
    }
  }, [state.open, state.body]);

  useEffect(() => {
    const node = footerRef.current;
    if (!node) return;
    node.innerHTML = "";
    if (state.footer != null && state.footer !== false) appendChildren(node, state.footer);
  }, [state.footer]);

  useEffect(() => {
    if (state.open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [state.open]);

  // close action: onClose가 있으면 호출자가 정의한 동작(예: 부모 시트로 복귀),
  // 없으면 기본 닫기.
  function dismiss() {
    if (state.onClose) state.onClose();
    else setSheetState({ open: false });
  }

  useEffect(() => {
    if (!state.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [state.open, state.onClose]);

  const hasFooter = state.footer != null && state.footer !== false;

  return (
    <Fragment>
      <div
        class="sheet-backdrop"
        hidden={!state.open}
        onClick={dismiss}
      />
      <aside
        class="sheet"
        hidden={!state.open}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
      >
        <div class="sheet__handle" aria-hidden="true" />
        <header class="sheet__header">
          <h2 class="sheet__title" id="sheet-title">{state.title}</h2>
          <button
            type="button"
            class="sheet__close"
            aria-label="닫기"
            onClick={dismiss}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div class="sheet__body" ref={bodyRef} />
        <div class="sheet__footer" hidden={!hasFooter} ref={footerRef} />
      </aside>
    </Fragment>
  );
}

export function mountSheet(container: HTMLElement): void {
  render(h(Sheet, {}), container);
}
